import { apiClient } from './client';

/**
 * S4-23 (I18N-CONFIRM): API раздела «Язык и регион».
 *
 * Mirror'ит контракт BFF (`apps/user-panel/src/modules/profile-locale/`),
 * который проксирует user-service. `userId` сервер резолвит из JWT —
 * фронт никогда не передаёт ID.
 */

export const LOCALE_AXES = ['language', 'country', 'currency'] as const;
export type LocaleAxis = (typeof LOCALE_AXES)[number];

/**
 * Источник значения оси — зеркало SSOT `@ecosystem/common`
 * (`libs/common/src/profile/locale-axis-source.ts`). Импортировать оттуда
 * нельзя: фронт — отдельный пакет без доступа к монорепо-алиасам.
 *
 * ⚠ `rejected` — это СОСТОЯНИЕ, а не отсутствие значения. `value` при нём
 * тоже `null`, но, в отличие от «не задано» (`source === null`),
 * автоопределение по этой оси больше НЕ применяется никогда, и карточку
 * подтверждения показывать нельзя. Схлопывать эти два случая в «нет
 * значения» запрещено — человек перестанет понимать, почему система то
 * переспрашивает, то молчит.
 */
export type LocaleAxisSource = 'detected_pending' | 'confirmed' | 'explicit' | 'rejected';

/** Реакция человека на ось. `value` обязателен И только для `explicit`. */
export type LocaleAxisAction = 'confirm' | 'reject' | 'explicit';

export interface ProfileLocale {
  language: string | null;
  languageSource: LocaleAxisSource | null;
  country: string | null;
  countrySource: LocaleAxisSource | null;
  currency: string | null;
  currencySource: LocaleAxisSource | null;
}

export interface LocaleAxisResult {
  axis: LocaleAxis;
  value: string | null;
  source: LocaleAxisSource | null;
}

/** @ensures 200 всегда — профиля может не быть, тогда все поля `null`. */
export async function getProfileLocale(): Promise<ProfileLocale> {
  const response = await apiClient.get<ProfileLocale>('/profile/locale');
  return response.data;
}

/**
 * @ensures Возвращает НОВОЕ состояние ровно одной оси — секция обновляется
 *   из ответа, без повторного полного перезапроса и без угадывания источника
 *   на клиенте (источник назначает user-service).
 */
export async function updateLocaleAxis(
  axis: LocaleAxis,
  action: LocaleAxisAction,
  value?: string,
): Promise<LocaleAxisResult> {
  // `value` кладём в тело ТОЛЬКО для explicit: BFF-DTO работает под
  // whitelist + forbidNonWhitelisted, и лишнее поле превратилось бы в
  // молчаливый 400.
  const body = action === 'explicit' ? { action, value } : { action };
  const response = await apiClient.patch<LocaleAxisResult>(`/profile/locale/${axis}`, body);
  return response.data;
}

/**
 * Коды языков, доступные для ЯВНОГО выбора.
 *
 * ⚠ Зеркало `DEFAULT_SUPPORTED_LANGUAGES` из master-agent
 * (`src/locale/locale.config.ts`) — единственного места, где живёт список
 * поддерживаемых языков ассистента. Импортировать его нельзя (другой
 * deployable), а список UI-переводов панели (`languages` в `src/i18n.ts`) —
 * ДРУГОЙ домен: там языки, на которые переведён интерфейс, здесь — языки,
 * на которых отвечает ассистент.
 *
 * Расхождение не ломает UI: значение, пришедшее с сервера вне этого списка,
 * добавляется в селект отдельной опцией (см. `LocaleSection`), поэтому
 * ops-override `I18N_SUPPORTED_LANGUAGES` не делает ось непредставимой.
 */
export const PROFILE_LANGUAGE_CODES = ['ru', 'en', 'es'] as const;

/**
 * Короткий список стран/валют для явного выбора: платформенная аудитория
 * (RU/СНГ) + страны, откуда реально приходят пользователи. Это НЕ каталог
 * ISO — полный список из 249 стран в селекте бесполезен, а сервер примет
 * любой валидный код (валидирует user-service).
 */
export const PROFILE_COUNTRY_CODES = [
  'RU',
  'BY',
  'KZ',
  'UZ',
  'AM',
  'GE',
  'TR',
  'AE',
  'DE',
  'ES',
  'FR',
  'GB',
  'US',
] as const;

export const PROFILE_CURRENCY_CODES = [
  'RUB',
  'USD',
  'EUR',
  'KZT',
  'GBP',
  'TRY',
  'AED',
  'GEL',
] as const;

/** Коды, предлагаемые для явного выбора по каждой оси. */
export const LOCALE_AXIS_CODES: Record<LocaleAxis, readonly string[]> = {
  language: PROFILE_LANGUAGE_CODES,
  country: PROFILE_COUNTRY_CODES,
  currency: PROFILE_CURRENCY_CODES,
};
